/* ==============================================================================
   Casca de configurações — trilha de ícones + navegação de duas linhas + conteúdo
   ==============================================================================
   O problema que esta estrutura resolve
   =====================================
   A tela de configurações anterior era uma coluna de sete rótulos de uma linha
   ("Voz e áudio", "Vídeo e tela", "Aparência") e uma área de conteúdo. Funciona
   com três itens e desmonta com sete: "Privacidade e Segurança" e "Avançado" não
   dizem o que têm dentro, e a pessoa abre os dois para descobrir onde fica o log.

   Três colunas, cada uma com um papel distinto:

     TRILHA (48px)   grupos de nível mais alto — preferências do app, conta.
                     Só ícone, com barra de acento vertical no ativo. É o eixo
                     que quase nunca se troca.
     NAVEGAÇÃO       cada item com TÍTULO e SUBTÍTULO. O subtítulo lista o que
                     tem dentro ("Aparência Idioma Inicialização"), o que
                     elimina a exploração por tentativa.
     CONTEÚDO        os controles, com micro-rótulo acima de cada um.

   Por que o subtítulo não é tooltip
   =================================
   Tooltip exige apontar e esperar, item por item. Sete itens são sete esperas
   para responder uma pergunta ("onde mudo o tema?") que a lista pode responder
   de uma vez só. O custo é altura: cada item passa de 32px para 48px. Vale, e é
   o que a referência mostra.

   A barra vertical de acento na trilha, e não fundo cheio
   ======================================================
   Uma pastilha preenchida de 48x48 na trilha vira um bloco de cor grande ao lado
   de uma lista discreta, e puxa o olho para o eixo que menos muda. A barra de
   3px marca sem pesar — é a mesma lógica do indicador de aba.
   ============================================================================== */

import { useEffect, useState, type ReactNode } from 'react';

/* ==============================================================================
   Modelo
============================================================================== */

export interface SettingsRailItem {
  id: string;
  /** Rótulo acessível e tooltip. A trilha é só ícone. */
  label: string;
  icon: ReactNode;
}

/** Tom do ícone do item. Colore só o ícone, nunca o texto. */
export type SettingsTone =
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral';

export interface SettingsNavItem {
  id: string;
  title: string;
  /** Segunda linha: o que existe dentro. Ex.: "Aparência Idioma Inicialização". */
  subtitle?: string;
  icon?: ReactNode;
  tone?: SettingsTone;
  /** Trilha a que pertence. Ausente = aparece em qualquer trilha. */
  railId?: string;
}

export interface SettingsShellProps {
  /** Título acima da navegação. */
  title?: string;
  subtitle?: string;

  rail?: ReadonlyArray<SettingsRailItem>;
  activeRailId?: string | undefined;
  onSelectRail?: ((id: string) => void) | undefined;

  items: ReadonlyArray<SettingsNavItem>;
  activeItemId: string;
  onSelectItem: (id: string) => void;

  /** Conteúdo da coluna direita. */
  children: ReactNode;
  /** Rodapé fixo do conteúdo. Use `SettingsFooter`. */
  footer?: ReactNode;

  onClose?: (() => void) | undefined;

  /**
   * `overlay` desenha painel flutuante sobre cortina desfocada — é o que a
   * referência mostra. `inline` preenche o contêiner, para quem já tem uma rota
   * dedicada de tela cheia.
   */
  variant?: 'overlay' | 'inline';
}

const TONE_COLOR: Record<SettingsTone, string> = {
  accent: 'var(--color-accent-on-subtle)',
  success: 'var(--color-success)',
  warning: 'var(--color-warning)',
  danger: 'var(--color-danger-on-subtle)',
  info: 'var(--color-info)',
  neutral: 'var(--color-text-tertiary)',
};

const TONE_BG: Record<SettingsTone, string> = {
  accent: 'var(--color-accent-subtle)',
  success: 'var(--color-success-subtle)',
  warning: 'var(--color-warning-subtle)',
  danger: 'var(--color-danger-subtle)',
  info: 'oklch(70% 0.150 235 / 0.14)',
  neutral: 'oklch(100% 0 0 / 0.06)',
};

/* ==============================================================================
   Componente
============================================================================== */

export function SettingsShell({
  title = 'Configurações',
  subtitle,
  rail = [],
  activeRailId,
  onSelectRail,
  items,
  activeItemId,
  onSelectItem,
  children,
  footer,
  onClose,
  variant = 'overlay',
}: SettingsShellProps) {
  /*
   * Escape fecha, e o ouvinte é registrado na fase de CAPTURA.
   *
   * Um popover aberto dentro deste painel também escuta Escape. Sem captura, a
   * ordem de recebimento depende da ordem de montagem — e o sintoma é o painel
   * inteiro fechar quando a pessoa só queria fechar o menu de dentro.
   */
  useEffect(() => {
    if (!onClose) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const visibleItems =
    activeRailId === undefined
      ? items
      : items.filter((item) => item.railId === undefined || item.railId === activeRailId);

  const body = (
    <div className="flex min-h-0 flex-1">
      {/* ====================================================================
          TRILHA
      ==================================================================== */}
      {rail.length > 0 && (
        <nav
          aria-label="Grupos de configuração"
          className="flex shrink-0 flex-col items-center py-4"
          style={{
            width: 56,
            gap: 'var(--spacing-2)',
            /* Aresta de luz interna em vez de `border-right`. Uma linha sólida
               aqui cortaria o painel em dois e desfaria a peça única. */
            boxShadow: 'inset -1px 0 0 oklch(100% 0 0 / 0.06)',
          }}
        >
          {rail.map((entry) => {
            const active = entry.id === activeRailId;
            return (
              <div key={entry.id} className="relative flex w-full justify-center">
                {active && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r-full"
                    style={{
                      width: 3,
                      height: 22,
                      background: 'var(--color-accent)',
                      boxShadow: '0 0 10px var(--color-accent-glow)',
                    }}
                  />
                )}
                <button
                  type="button"
                  onClick={() => onSelectRail?.(entry.id)}
                  aria-current={active ? 'true' : undefined}
                  title={entry.label}
                  className="grid place-items-center transition-colors"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 'var(--radius-lg)',
                    background: active ? 'var(--color-accent-subtle)' : 'transparent',
                    color: active
                      ? 'var(--color-accent-on-subtle)'
                      : 'var(--color-text-tertiary)',
                  }}
                >
                  {entry.icon}
                  <span className="sr-only">{entry.label}</span>
                </button>
              </div>
            );
          })}
        </nav>
      )}

      {/* ====================================================================
          NAVEGAÇÃO
      ==================================================================== */}
      <nav
        aria-label="Seções"
        className="scroll-hover-only flex shrink-0 flex-col overflow-y-auto px-2 py-3"
        style={{
          width: 244,
          gap: 2,
          boxShadow: 'inset -1px 0 0 oklch(100% 0 0 / 0.06)',
        }}
      >
        <div className="mb-2 px-2">
          <h2 className="font-bold"
              style={{
                fontSize: 'var(--text-md)',
                letterSpacing: 'var(--tracking-tight)',
                color: 'var(--color-text-primary)',
              }}>
            {title}
          </h2>
          {subtitle !== undefined && (
            <p style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
              {subtitle}
            </p>
          )}
        </div>

        {visibleItems.map((item) => (
          <NavRow
            key={item.id}
            item={item}
            active={item.id === activeItemId}
            onClick={() => onSelectItem(item.id)}
          />
        ))}

        {onClose && (
          <>
            <span aria-hidden="true" className="my-2 block"
                  style={{ height: 1, background: 'var(--color-border-subtle)' }} />
            <button
              type="button"
              onClick={onClose}
              className="flex items-center px-3 py-2 text-left transition-colors"
              style={{
                gap: 'var(--spacing-2)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.6"
                      strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Voltar
            </button>
          </>
        )}
      </nav>

      {/* ====================================================================
          CONTEÚDO
      ==================================================================== */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="scroll-hover-only min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>
        {footer !== undefined && (
          <div className="shrink-0 px-6 py-3"
               style={{
                 /* Aresta de luz e um véu escuro: o rodapé é uma faixa DENTRO do
                    painel, e uma linha sólida ali o transformaria em segundo
                    painel encostado. */
                 boxShadow: 'inset 0 1px 0 oklch(100% 0 0 / 0.07)',
                 background: 'oklch(0% 0 0 / 0.16)',
               }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className="ambient flex h-full min-h-0 w-full flex-col overflow-hidden">
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 grid place-items-center p-4"
         style={{ zIndex: 'var(--z-modal)' }}>
      <style>{`
        @keyframes settings-in {
          from { opacity: 0; transform: translateY(14px) scale(0.985) }
          to   { opacity: 1; transform: none }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes settings-in { from { opacity: 0 } to { opacity: 1 } }
        }
      `}</style>

      {onClose && (
        <button
          type="button"
          aria-label="Fechar configurações"
          onClick={onClose}
          className="absolute inset-0 cursor-default"
          style={{
            background: 'oklch(4% 0.02 292 / 0.62)',
            backdropFilter: 'blur(12px) saturate(140%)',
            WebkitBackdropFilter: 'blur(12px) saturate(140%)',
          }}
        />
      )}

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="glass noise relative flex w-full flex-col overflow-hidden"
        style={{
          maxWidth: 960,
          height: 'min(84vh, 700px)',
          borderRadius: 'var(--radius-2xl)',
          boxShadow:
            'var(--glass-edge), 0 32px 90px oklch(0% 0 0 / 0.60), var(--glow-medium)',
          animation: 'settings-in var(--duration-normal) var(--ease-out-expo) both',
        }}
      >
        {body}
      </div>
    </div>
  );
}

/* ==============================================================================
   Item de navegação — duas linhas
============================================================================== */

function NavRow({
  item,
  active,
  onClick,
}: {
  item: SettingsNavItem;
  active: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);
  const tone = item.tone ?? 'neutral';

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-current={active ? 'true' : undefined}
      className="flex w-full items-center px-2.5 py-2 text-left transition-colors"
      style={{
        gap: 'var(--spacing-3)',
        borderRadius: 'var(--radius-lg)',
        /* Pastilha de acento TRANSLÚCIDA no ativo, e não a superfície de hover:
           o item aberto precisa ser distinguível do item sob o cursor, senão a
           lista mostra dois "selecionados" ao mesmo tempo. */
        background: active
          ? 'var(--color-accent-subtle)'
          : hover
            ? 'oklch(100% 0 0 / 0.05)'
            : 'transparent',
        boxShadow: active ? 'inset 0 1px 0 oklch(100% 0 0 / 0.08)' : 'none',
        transitionDuration: 'var(--duration-fast)',
      }}
    >
      {item.icon !== undefined && (
        <span
          aria-hidden="true"
          className="grid shrink-0 place-items-center"
          style={{
            width: 30,
            height: 30,
            borderRadius: 'var(--radius-md)',
            background: TONE_BG[tone],
            color: TONE_COLOR[tone],
          }}
        >
          {item.icon}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold leading-tight"
              style={{
                fontSize: 'var(--text-xs)',
                color: active
                  ? 'var(--color-text-primary)'
                  : 'var(--color-text-secondary)',
              }}>
          {item.title}
        </span>
        {item.subtitle !== undefined && (
          <span className="block truncate leading-tight"
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-tertiary)',
                }}>
            {item.subtitle}
          </span>
        )}
      </span>

      {active && (
        <span aria-hidden="true" className="shrink-0"
              style={{ color: 'var(--color-accent)' }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      )}
    </button>
  );
}

/* ==============================================================================
   Peças de conteúdo
============================================================================== */

/**
 * Seção do conteúdo: micro-rótulo em caixa alta, título, e um fio ACIMA.
 *
 * O fio vem antes da seção e não depois: separador depois do último bloco
 * desenha uma linha solta no fim da página, sem nada abaixo para separar. A
 * primeira seção não recebe fio.
 */
export function SettingsSection({
  label,
  title,
  description,
  first = false,
  children,
}: {
  /** Micro-rótulo acinzentado. Ex.: "Seletor de Tema". */
  label?: string;
  /** Título da seção. Ex.: "Escolha seu Tema". */
  title?: string;
  description?: string;
  first?: boolean;
  children?: ReactNode;
}) {
  return (
    <section
      className={first ? '' : 'mt-6 pt-6'}
      style={
        first
          ? undefined
          : { boxShadow: 'inset 0 1px 0 var(--color-border-subtle)' }
      }
    >
      {label !== undefined && (
        <p className="mb-1 font-semibold uppercase"
           style={{
             fontSize: 'var(--text-2xs)',
             letterSpacing: 'var(--tracking-wide)',
             color: 'var(--color-text-tertiary)',
           }}>
          {label}
        </p>
      )}
      {title !== undefined && (
        <h3 className="font-semibold"
            style={{
              fontSize: 'var(--text-base)',
              letterSpacing: 'var(--tracking-tight)',
              color: 'var(--color-text-primary)',
            }}>
          {title}
        </h3>
      )}
      {description !== undefined && (
        <p className="mt-1"
           style={{
             fontSize: 'var(--text-xs)',
             lineHeight: 'var(--leading-relaxed)',
             color: 'var(--color-text-tertiary)',
             maxWidth: '58ch',
           }}>
          {description}
        </p>
      )}
      {children !== undefined && <div className="mt-3">{children}</div>}
    </section>
  );
}

/** Linha de controle: rótulo e descrição à esquerda, controle à direita. */
export function SettingsRow({
  label,
  description,
  children,
  stacked = false,
}: {
  label: string;
  description?: string;
  children: ReactNode;
  /** Controle largo (slider, paleta) vai ABAIXO do rótulo, não ao lado. */
  stacked?: boolean;
}) {
  return (
    <div
      className={stacked ? 'py-2' : 'flex items-center justify-between py-2'}
      style={stacked ? undefined : { gap: 'var(--spacing-4)' }}
    >
      <div className="min-w-0">
        <p className="font-medium"
           style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
          {label}
        </p>
        {description !== undefined && (
          <p style={{
               fontSize: 'var(--text-2xs)',
               color: 'var(--color-text-tertiary)',
               lineHeight: 'var(--leading-normal)',
             }}>
            {description}
          </p>
        )}
      </div>
      <div className={stacked ? 'mt-2' : 'shrink-0'}>{children}</div>
    </div>
  );
}

/**
 * Rodapé: ação primária preenchida + restauração como LINK.
 *
 * "Restaurar padrões" como link e não como segundo botão é deliberado: dois
 * botões lado a lado com pesos parecidos fazem a pessoa escolher entre eles, e
 * aqui um dos dois DESCARTA o que ela acabou de configurar. O link continua
 * alcançável e para de parecer a outra metade de uma escolha.
 */
export function SettingsFooter({
  onSave,
  onReset,
  saving = false,
  dirty = true,
  saveLabel = 'Salvar Alterações',
  resetLabel = 'Restaurar Padrões',
  hint,
}: {
  onSave: () => void;
  onReset?: (() => void) | undefined;
  saving?: boolean;
  dirty?: boolean;
  saveLabel?: string;
  resetLabel?: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between"
         style={{ gap: 'var(--spacing-3)' }}>
      <div className="min-w-0">
        {hint !== undefined && (
          <p className="truncate"
             style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
            {hint}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center" style={{ gap: 'var(--spacing-4)' }}>
        {onReset && (
          <button
            type="button"
            onClick={onReset}
            className="font-medium underline-offset-4 hover:underline"
            style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}
          >
            {resetLabel}
          </button>
        )}
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !dirty}
          className="px-4 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-45"
          style={{
            height: 36,
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-accent-solid)',
            color: 'var(--color-on-solid)',
            fontSize: 'var(--text-xs)',
            boxShadow: dirty && !saving ? 'var(--glow-medium)' : 'none',
          }}
        >
          {saving ? 'Salvando…' : saveLabel}
        </button>
      </div>
    </div>
  );
}

/* ==============================================================================
   Abas horizontais — usadas pelo modal de configurações de servidor
   ==============================================================================
   Indicador que DESLIZA em vez de aparecer sob a aba nova.

   Aparecer e desaparecer em lugares diferentes obriga o olho a reencontrar o
   indicador; deslizar carrega a atenção de onde ela estava até onde precisa
   estar. É um `transform` sobre uma única barra, não uma barra por aba — animar
   `left` em quatro elementos custaria quatro reflows por troca.
============================================================================== */

export interface SettingsTab {
  id: string;
  label: string;
  /** Contador opcional à direita do rótulo. */
  count?: number;
}

export function SettingsTabs({
  tabs,
  activeId,
  onSelect,
}: {
  tabs: ReadonlyArray<SettingsTab>;
  activeId: string;
  onSelect: (id: string) => void;
}) {
  const index = Math.max(0, tabs.findIndex((tab) => tab.id === activeId));

  return (
    <div role="tablist" className="relative flex items-center"
         style={{ gap: 'var(--spacing-1)' }}>
      {tabs.map((tab, position) => {
        const active = position === index;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onSelect(tab.id)}
            className="relative flex items-center px-3 font-medium transition-colors"
            style={{
              gap: 'var(--spacing-2)',
              height: 34,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-xs)',
              color: active
                ? 'var(--color-text-primary)'
                : 'var(--color-text-tertiary)',
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className="grid place-items-center px-1.5 font-semibold"
                style={{
                  minWidth: 18,
                  height: 16,
                  borderRadius: 'var(--radius-full)',
                  background: active
                    ? 'var(--color-accent-subtle)'
                    : 'oklch(100% 0 0 / 0.06)',
                  color: active
                    ? 'var(--color-accent-on-subtle)'
                    : 'var(--color-text-tertiary)',
                  fontSize: 9,
                }}
              >
                {tab.count}
              </span>
            )}
            {active && (
              <span
                aria-hidden="true"
                className="absolute inset-x-2 rounded-full"
                style={{
                  bottom: -1,
                  height: 2,
                  background: 'var(--color-accent)',
                  boxShadow: '0 0 10px var(--color-accent-glow)',
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ==============================================================================
   Conjunto de navegação da referência
   ==============================================================================
   Exportado como CONSTANTE e não embutido no componente.

   Embutido, a casca ficaria acoplada a estes sete itens e serviria só a esta
   tela — o modal de servidor tem outros. Como dado, o consumidor monta a lista
   que precisa e ainda tem a da referência pronta para colar.
============================================================================== */

export const SETTINGS_RAIL_PRESET: ReadonlyArray<SettingsRailItem> = [
  { id: 'app', label: 'Preferências do aplicativo', icon: <GearIcon /> },
  { id: 'account', label: 'Conta e perfil', icon: <PersonIcon /> },
];

export const SETTINGS_NAV_PRESET: ReadonlyArray<SettingsNavItem> = [
  {
    id: 'general',
    title: 'Geral',
    subtitle: 'Aparência · Idioma · Inicialização',
    tone: 'accent',
    railId: 'app',
    icon: <SlidersIcon />,
  },
  {
    id: 'profile',
    title: 'Perfil e Conta',
    subtitle: 'Perfil · Segurança · Assinatura',
    tone: 'info',
    railId: 'account',
    icon: <PersonIcon />,
  },
  {
    id: 'notifications',
    title: 'Notificações',
    subtitle: 'Push · E-mail · Sons',
    tone: 'warning',
    railId: 'app',
    icon: <BellIcon />,
  },
  {
    id: 'media',
    title: 'Áudio e Vídeo',
    subtitle: 'Dispositivos · Qualidade',
    tone: 'success',
    railId: 'app',
    icon: <WaveIcon />,
  },
  {
    id: 'integrations',
    title: 'Integrações',
    subtitle: 'Apps conectados · Bots',
    tone: 'accent',
    railId: 'app',
    icon: <PlugIcon />,
  },
  {
    id: 'privacy',
    title: 'Privacidade e Segurança',
    subtitle: 'Configurações de privacidade',
    tone: 'danger',
    railId: 'account',
    icon: <ShieldIcon />,
  },
  {
    id: 'advanced',
    title: 'Avançado',
    subtitle: 'Desenvolvedor · Logs',
    tone: 'neutral',
    railId: 'app',
    icon: <TerminalIcon />,
  },
];

/* ==============================================================================
   Ícones
============================================================================== */

function GearIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 2.4v2M10 15.6v2M2.4 10h2M15.6 10h2M4.6 4.6l1.4 1.4M14 14l1.4 1.4M15.4 4.6L14 6M6 14l-1.4 1.4"
            stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="6.8" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path d="M4 17c0-3.1 2.7-5 6-5s6 1.9 6 5" stroke="currentColor"
            strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SlidersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 6h9M15 6h2M3 14h4M10 14h7" stroke="currentColor"
            strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="13.6" cy="6" r="1.8" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="8.4" cy="14" r="1.8" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5.4 8.4a4.6 4.6 0 019.2 0v3l1.2 2.2H4.2L5.4 11.4v-3z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8.2 15.8a1.9 1.9 0 003.6 0" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function WaveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 10h1.6M7 6.4v7.2M10 4v12M13 7.2v5.6M16.4 10H18"
            stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function PlugIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M7.6 3v3.4M12.4 3v3.4" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" />
      <path d="M5.6 6.4h8.8v3a4.4 4.4 0 11-8.8 0v-3z" stroke="currentColor"
            strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 13.8V17" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 2.8l5.6 2v4.6c0 3.4-2.3 6.2-5.6 7.4-3.3-1.2-5.6-4-5.6-7.4V4.8l5.6-2z"
            stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M7.6 10l1.8 1.8 3.2-3.4" stroke="currentColor" strokeWidth="1.6"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TerminalIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2.6" y="3.6" width="14.8" height="12.8" rx="2"
            stroke="currentColor" strokeWidth="1.5" />
      <path d="M5.8 8l2 2-2 2M9.8 12.4h4" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
